import { useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Share2 } from "lucide-react";
import ShareSheet from "@/components/ShareSheet";
import { useAuth } from "@/contexts/AuthContext";

/**
 * GlobalShareButton — top-right share button that's context-aware.
 *
 * - On an event page (/events/:id) → shares that event
 * - On a group page (/groups/:id) → shares that group
 * - Anywhere else → opens an app-level share (Born Day itself, with the
 *   current user listed as the inviter so the recipient knows who sent it)
 *
 * Falls back to the OS native share sheet via ShareSheet's `navigator.share`
 * hero CTA on iPhone/Android.
 */
export default function GlobalShareButton() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const params = useParams();
  const { user } = useAuth();

  // Detect context from the current URL
  const eventMatch = location.pathname.match(/^\/events\/([^/?#]+)/);
  const groupMatch = location.pathname.match(/^\/groups\/([^/?#]+)/);

  const ctx = eventMatch
    ? { kind: "event", id: eventMatch[1] }
    : groupMatch
      ? { kind: "group", id: groupMatch[1] }
      : { kind: "app", id: null };

  const label = ctx.kind === "event"
    ? "Share this event"
    : ctx.kind === "group"
      ? "Share this group"
      : "Share Born Day";

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="bd-btn bd-btn-ghost"
        style={{ padding: "0.45rem 0.7rem", fontSize: "0.85rem" }}
        title={label}
        aria-label={label}
        data-testid="global-share-btn"
      >
        <Share2 size={14} />
        <span className="hidden md:inline" style={{ fontSize: "0.8rem" }}>Share</span>
      </button>

      {open && ctx.kind === "event" && (
        <ShareSheet
          eventId={ctx.id}
          inviterName={user?.name}
          onClose={() => setOpen(false)}
        />
      )}
      {open && ctx.kind === "group" && (
        <ShareSheet
          groupId={ctx.id}
          inviterName={user?.name}
          onClose={() => setOpen(false)}
        />
      )}
      {open && ctx.kind === "app" && (
        <AppShareSheet
          inviterName={user?.name}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/**
 * AppShareSheet — lightweight ShareSheet variant for "share Born Day"
 * (no event/group context). Reuses native-share + copy-link + social channels.
 */
function AppShareSheet({ inviterName, onClose }) {
  const link = typeof window !== "undefined" ? window.location.origin : "https://bornday.me";
  // Minimum-character footprint per Steve (Feb 2026). Both the long
  // and short channels reuse the same one-line invite.
  const inviter = inviterName || "A friend";
  const shareText = `${inviter} invited you to Born Day: ${link}`;
  const shortText = shareText;
  const nativeSupported = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.warn("Clipboard write failed:", err);
    }
  };

  const nativeShare = async () => {
    try {
      await navigator.share({ title: "Born Day", text: shareText, url: link });
    } catch (err) {
      if (err?.name !== "AbortError") console.warn("Native share failed:", err);
    }
  };

  const channels = [
    { id: "sms", label: "Text (SMS)", color: "#34C759",
      action: () => {
        const body = encodeURIComponent(shareText);
        // sms:?body=... opens the OS messaging app — works on iOS, Android, and modern desktops.
        window.location.href = `sms:?&body=${body}`;
      } },
    { id: "whatsapp", label: "WhatsApp", color: "#25D366",
      action: () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank") },
    { id: "email", label: "Email", color: "#888888",
      action: () => {
        const subject = encodeURIComponent("Invite: Born Day");
        const body = encodeURIComponent(shareText);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
      } },
    { id: "facebook", label: "Facebook", color: "#1877F2",
      action: () => window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}&quote=${encodeURIComponent(shortText)}`,
        "_blank", "noopener,noreferrer,width=600,height=600") },
    { id: "x", label: "X (Twitter)", color: "#000000",
      action: () => window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(shortText)}`,
        "_blank", "noopener,noreferrer,width=600,height=500") },
    { id: "linkedin", label: "LinkedIn", color: "#0A66C2",
      action: () => window.open(
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`,
        "_blank", "noopener,noreferrer,width=600,height=600") },
  ];

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
      data-testid="app-share-sheet"
    >
      <div
        className="bd-surface p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-2xl font-bold" style={{ letterSpacing: "-0.03em" }}>Share Born Day</h2>
          <button onClick={onClose} className="bd-btn bd-btn-ghost" style={{ padding: "0.4rem" }} data-testid="app-share-close">
            ×
          </button>
        </div>

        {nativeSupported && (
          <button
            onClick={nativeShare}
            className="bd-btn w-full mb-3"
            style={{
              padding: "0.9rem 1.2rem", fontSize: "1rem", fontWeight: 700,
              boxShadow: "0 4px 14px rgba(255, 122, 89, 0.3)",
            }}
            data-testid="app-share-native"
          >
            <Share2 size={16} /> Share
          </button>
        )}

        <div className="grid grid-cols-[1fr_auto] gap-2 mb-4">
          <input className="bd-input bd-mono" readOnly value={link} data-testid="app-share-link" />
          <button onClick={copy} className="bd-btn" data-testid="app-share-copy">
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {channels.map((c) => (
            <button
              key={c.id}
              onClick={c.action}
              className="bd-card flex flex-col items-center gap-1 py-3"
              data-testid={`app-share-${c.id}`}
            >
              <span
                className="w-8 h-8 grid place-items-center rounded-full text-white font-bold"
                style={{ background: c.color }}
              >
                {c.label[0]}
              </span>
              <span className="text-xs font-semibold">{c.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
